import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import useDraggable from '../hooks/useDraggable';
import useTrackPageView from '../hooks/useTrackPageView';
import { useTranslation } from '../i18n/LanguageContext';

// RishHelp.jsx'teki AYNI küçük sunum bileşenleri (sh-* CSS sınıfları) — görsel tutarlılık için
// burada da birebir kullanılıyor.
const Section = ({ title, children }) => (
  <div className="sh-section">
    <h3 className="sh-section-title">{title}</h3>
    {children}
  </div>
);

const Param = ({ name, unit, recommended, recommendedLabel, range, rangeLabel, sweepEx, sweepLabel, children }) => (
  <div className="sh-param">
    <div className="sh-param-header">
      <span className="sh-param-name">{name}</span>
      {unit && <span className="sh-param-unit">{unit}</span>}
      {range && <span className="sh-param-unit">{rangeLabel} {range}</span>}
      {recommended && <span className="sh-param-rec">{recommendedLabel} {recommended}</span>}
    </div>
    <div className="sh-param-body">
      {children}
      {sweepEx && (
        <p style={{ marginTop: 4 }}>
          <strong>{sweepLabel}</strong>{' '}
          <span style={{ fontFamily: 'var(--mono, monospace)', background: 'var(--surface2)', borderRadius: 4, padding: '1px 6px' }}>{sweepEx}</span>
        </p>
      )}
    </div>
  </div>
);

const Example = ({ label, children }) => (
  <div className="sh-example">
    <span className="sh-example-label">{label}</span>
    <span className="sh-example-text">{children}</span>
  </div>
);

const Note = ({ children }) => (
  <div style={{ background: 'rgba(13,148,136,.08)', border: '1px solid rgba(13,148,136,.25)', borderRadius: 7, padding: '8px 12px', fontSize: 12, color: 'var(--accent)', marginTop: 8 }}>
    {children}
  </div>
);

const Warn = ({ children }) => (
  <div style={{ background: 'rgba(220,38,38,.06)', border: '1px solid rgba(220,38,38,.25)', borderRadius: 7, padding: '8px 12px', fontSize: 12, color: '#b91c1c', marginTop: 8 }}>
    {children}
  </div>
);

const Timeline = ({ steps }) => (
  <div className="sh-timeline">
    {steps.map((s, i) => (
      <div key={i} className="sh-timeline-step">
        <div className="sh-timeline-dot" style={{ background: s.color || 'var(--accent)' }} />
        <div className="sh-timeline-content">
          <div className="sh-timeline-label" style={{ color: s.color || 'var(--accent)' }}>{s.label}</div>
          <div className="sh-timeline-text">{s.text}</div>
        </div>
      </div>
    ))}
  </div>
);

// ── Tüm stratejilerde ortak (Parametre Taraması'nda her strateji için ayrıca görünen) alanlar ──
function CommonParams({ isEn }) {
  const rec = isEn ? 'Recommended:' : 'Önerilen:';
  const rng = isEn ? 'Range:' : 'Aralık:';
  const swp = isEn ? "Example in Parameter Sweep:" : "Parametre Taraması'nda örnek:";
  return (
    <Section title={isEn ? '⚙ Common Risk Parameters (apply to all strategies)' : '⚙ Ortak Risk Parametreleri (tüm stratejilerde geçerli)'}>
      <p className="sh-text">
        {isEn
          ? "These fields appear SEPARATELY under every strategy in Parameter Sweep — they do the same job no matter which strategy you pick. Fields left blank aren't swept (a single fixed value is used)."
          : "Bu alanlar Parametre Taraması'nda her stratejinin altında AYRICA görünür — hangi stratejiyi seçersen seç aynı işi yaparlar. Boş bıraktığın alanlar taranmaz (tek bir sabit değer kullanılır)."}
      </p>

      <Param name={isEn ? 'ATR Period' : 'ATR Periyodu'} unit="bar" range="7 – 30" recommended="14" sweepEx="9,14,21" recommendedLabel={rec} rangeLabel={rng} sweepLabel={swp}>
        <p>{isEn
          ? 'The basis for all ATR-based calculations like SL/TP/pullback. A lower value makes ATR more sensitive to recent price moves (adapts fast, choppier); a higher value produces a more stable/slower ATR.'
          : "SL/TP/pullback gibi tüm ATR-bazlı hesaplamaların temeli. Düşük değer ATR'yi son fiyat hareketlerine daha duyarlı yapar (hızlı adapte olur, dalgalı); yüksek değer daha kararlı/yavaş bir ATR üretir."}</p>
      </Param>

      <Param name={isEn ? 'SL Multiplier' : 'SL Çarpanı'} unit="× ATR" range="0.5 – 3.0" recommended="1.2" sweepEx="0.8,1.2,2.0" recommendedLabel={rec} rangeLabel={rng} sweepLabel={swp}>
        <p>{isEn
          ? 'Stop Loss distance = Entry ∓ (SL Multiplier × ATR). SL stays fixed after entry, never adjusted.'
          : 'Stop Loss mesafesi = Giriş ∓ (SL Çarpanı × ATR). SL giriş sonrası sabit kalır, hiç değişmez.'}</p>
        <Example label={isEn ? 'Low (0.5-0.8)' : 'Düşük (0.5-0.8)'}>{isEn ? 'Tight SL — less loss but even normal price noise carries a high risk of an early/wrong exit.' : 'Dar SL — az kayıp ama normal fiyat gürültüsüyle bile erken/yanlış çıkma riski yüksek.'}</Example>
        <Example label={isEn ? 'High (2.0-3.0)' : 'Yüksek (2.0-3.0)'}>{isEn ? 'Wide SL — resistant to noise but a bigger loss on a single losing trade.' : 'Geniş SL — gürültüye dayanıklı ama tek kayıp işlemde daha büyük zarar.'}</Example>
      </Param>

      <Param name={isEn ? 'SL Percentage' : 'SL Yüzdesi'} unit="%" range={isEn ? '0 (off) or 0.1 – 5' : '0 (kapalı) veya 0.1 – 5'} recommended={isEn ? '0 (ATR mode)' : '0 (ATR modu)'} sweepEx="0,0.5,1" recommendedLabel={rec} rangeLabel={rng} sweepLabel={swp}>
        <p>{isEn ? 'If set above zero, a fixed percentage of the entry price is used instead of ATR (SL Multiplier is disabled).' : 'Sıfırdan büyük girilirse ATR yerine giriş fiyatının sabit bir yüzdesi kullanılır (SL Çarpanı devre dışı kalır).'}</p>
      </Param>

      <Note>
        {isEn
          ? 'The CHOP (sideways market) filter, Min. Reward:Risk, and Daily Loss Limit are no longer user-configurable — they always run with their safe defaults (CHOP filter on at 61.8, the other two off) and are no longer swept.'
          : 'CHOP (yatay piyasa) filtresi, Min. Ödül:Risk ve Günlük Kayıp Limiti artık kullanıcı tarafından ayarlanamıyor — her zaman güvenli varsayılan değerleriyle çalışır (CHOP filtresi 61.8 ile açık, diğer ikisi kapalı) ve taranmıyor.'}
      </Note>
    </Section>
  );
}

function HmaAdxTab({ rangeBar, isEn }) {
  const rec = isEn ? 'Recommended:' : 'Önerilen:';
  const rng = isEn ? 'Range:' : 'Aralık:';
  const swp = isEn ? 'Example in Parameter Sweep:' : "Parametre Taraması'nda örnek:";
  return (
    <>
      <Section title={rangeBar
        ? (isEn ? '📈 Range Bar + HMA + ADX — Overview' : '📈 Range Bar + HMA + ADX — Genel Bakış')
        : (isEn ? '📈 HMA + ADX — Overview' : '📈 HMA + ADX — Genel Bakış')}>
        <p className="sh-text">
          {isEn ? (
            <>A state-based, instant trend-following strategy: it never waits for a flip, opening instantly in the current
            HMA direction whenever flat. <strong>There is no fixed TP</strong> — only SL (a safety net, unchanged after entry).
            {rangeBar && ' HMA/ADX here are calculated on range bars — which close once price moves a box size — instead of raw candle closes.'}</>
          ) : (
            <>Durum-tabanlı, anlık bir trend-takip stratejisi: flip beklemez, flat kaldığı HER an o anki
            HMA yönüne göre hemen açılır. <strong>Sabit TP yoktur</strong> — sadece SL (yedek sigorta,
            giriş sonrası değişmez).
            {rangeBar && ' HMA/ADX burada ham mum kapanışları yerine, fiyatın kutu (box) büyüklüğü kadar hareket ettiğinde kapanan range bar\'lar üzerinde hesaplanır.'}</>
          )}
        </p>
        <Timeline steps={isEn ? [
          { label: 'Entry (while flat)', text: 'Opens instantly in whatever direction HMA is pointing (also checking ADX if ADX Threshold>0).', color: '#3b82f6' },
          { label: 'Futures — HMA reverses', text: 'The position closes instantly and reopens immediately in the opposite direction on the same turn.', color: '#f59e0b' },
          { label: 'Spot — HMA turns down', text: 'An open LONG closes, stays flat (shorting isn\'t possible).', color: '#dc2626' },
        ] : [
          { label: 'Giriş (flat iken)', text: 'HMA yönü neyse (ADX Eşiği>0 ise ADX de uygunsa) o yönde hemen açılır.', color: '#3b82f6' },
          { label: 'Vadeli — HMA tersine döner', text: 'Pozisyon anında kapatılır ve AYNI turda ters yönde hemen yeniden açılır.', color: '#f59e0b' },
          { label: 'Spot — HMA aşağı döner', text: 'Açık LONG kapatılır, flat kalınır (short açılamaz).', color: '#dc2626' },
        ]} />
      </Section>

      <Section title={isEn ? '🔧 Parameters' : '🔧 Parametreler'}>
        <Param name={isEn ? 'HMA Period' : 'HMA Periyodu'} unit="bar" range="5 – 50" recommended="21" sweepEx="13,21,34" recommendedLabel={rec} rangeLabel={rng} sweepLabel={swp}>
          <p>{isEn ? 'Hull MA calculation length.' : 'Hull MA hesaplama uzunluğu.'}</p>
          <Example label={isEn ? 'Low (5-13)' : 'Düşük (5-13)'}>{isEn ? 'Very fast direction changes — frequent signals, prone to whipsaw.' : "Çok hızlı yön değişimi — sık sinyal, whipsaw'a açık."}</Example>
          <Example label={isEn ? 'High (34-50)' : 'Yüksek (34-50)'}>{isEn ? 'Slow, few signals — catches big trends but enters/exits late.' : 'Yavaş, az sinyal — büyük trendleri yakalar ama geç girer/çıkar.'}</Example>
        </Param>
        <Param name={isEn ? 'ADX Period' : 'ADX Periyodu'} unit="bar" range="7 – 30" recommended="14" sweepEx="9,14,21" recommendedLabel={rec} rangeLabel={rng} sweepLabel={swp}>
          <p>{isEn ? 'ADX calculation length — a lower value produces a more sensitive ADX, a higher value a more stable one.' : 'ADX hesaplama uzunluğu — düşük değer daha duyarlı, yüksek değer daha kararlı ADX üretir.'}</p>
        </Param>
        <Param name={isEn ? 'ADX Threshold' : 'ADX Eşiği'} range={isEn ? '0 (off) or 15 – 35' : '0 (kapalı) veya 15 – 35'} recommended="0" sweepEx="0,20,25" recommendedLabel={rec} rangeLabel={rng} sweepLabel={swp}>
          <p>{isEn
            ? <><strong>0:</strong> ADX filter is completely disabled, only the HMA direction is needed. <strong> &gt;0:</strong> both the HMA direction AND ADX ≥ threshold are required (also applies to the close decision).</>
            : <><strong>0:</strong> ADX filtresi tamamen devre dışı, sadece HMA yönü yeterli. <strong> &gt;0:</strong> HMA yönü VE ADX ≥ eşik ikisi birden aranır (kapanış kararında da geçerli).</>}</p>
        </Param>
        {rangeBar && (
          <Param name={isEn ? 'Box Multiplier' : 'Box Çarpanı'} unit="× ATR" range="0.3 – 3.0" recommended="1.0" sweepEx="0.5,1.0,2.0" recommendedLabel={rec} rangeLabel={rng} sweepLabel={swp}>
            <p>{isEn ? 'Range bar box size = ATR × this multiplier.' : 'Range bar kutu büyüklüğü = ATR × bu çarpan.'}</p>
            <Example label={isEn ? 'Low (0.3-0.6)' : 'Düşük (0.3-0.6)'}>{isEn ? 'Bars close frequently — captures intra-candle moves but prone to noise, trade count increases.' : "Bar'lar sık kapanır — mum içi hareketleri yakalar ama gürültüye açık, işlem sayısı artar."}</Example>
            <Example label={isEn ? 'High (1.5-3.0)' : 'Yüksek (1.5-3.0)'}>{isEn ? 'Bars close rarely — fewer but more meaningful signals, no bar may form for a long time in a calm market.' : "Bar'lar seyrek kapanır — daha az ama daha anlamlı sinyal, sakin piyasada uzun süre hiç bar oluşmayabilir."}</Example>
          </Param>
        )}
      </Section>
    </>
  );
}

function RsiRsimaTab({ isEn }) {
  const rec = isEn ? 'Recommended:' : 'Önerilen:';
  const rng = isEn ? 'Range:' : 'Aralık:';
  const swp = isEn ? 'Example in Parameter Sweep:' : "Parametre Taraması'nda örnek:";
  return (
    <>
      <Section title={isEn ? '📊 RSI + SMA — Overview' : '📊 RSI + SMA — Genel Bakış'}>
        <p className="sh-text">
          {isEn ? (
            <>Entry is <strong>unconditional and instant</strong> — the dead zone is NOT an entry gate. While flat, whichever
            side RSI is on relative to its own 14-period moving average (SMA), it opens instantly in that direction.
            <strong> TP and SL are both optional</strong> and fully independent — 0/blank on either one means it's OFF.
            Unlike other strategies, there is <strong>no ATR safety-net fallback</strong> here: if SL is left at 0/blank,
            the position has no fixed price protection at all, closing only via the SMA flip.</>
          ) : (
            <>Giriş <strong>koşulsuz ve anlıktır</strong> — dead zone bir giriş kapısı DEĞİLDİR. Flat iken
            RSI, kendi 14 periyotluk hareketli ortalamasına (SMA) göre hangi taraftaysa hemen o
            yönde açılır. <strong>TP ve SL ikisi de opsiyoneldir</strong> ve birbirinden tamamen
            bağımsızdır — herhangi biri 0/boş bırakılırsa KAPALI demektir. Diğer stratejilerin aksine
            burada <strong>ATR yedek-sigorta fallback'ı YOKTUR</strong>: SL 0/boş bırakılırsa pozisyonun
            hiçbir sabit fiyat koruması olmaz, sadece SMA flip'iyle kapanır.</>
          )}
        </p>
        <div className="sh-rsi-grid">
          <div className="sh-rsi-card sh-rsi-long">
            <div className="sh-rsi-card-title">▲ LONG</div>
            <div className="sh-rsi-range">RSI ≥ SMA</div>
            <ul className="sh-rsi-list">
              <li>{isEn ? 'Exit' : 'Çıkış'}: RSI ≤ SMA − {isEn ? 'Dead Zone' : 'Ölü Bölge'}</li>
            </ul>
          </div>
          <div className="sh-rsi-card sh-rsi-short">
            <div className="sh-rsi-card-title">▼ SHORT</div>
            <div className="sh-rsi-range">RSI &lt; SMA</div>
            <ul className="sh-rsi-list">
              <li>{isEn ? 'Exit' : 'Çıkış'}: RSI ≥ SMA + {isEn ? 'Dead Zone' : 'Ölü Bölge'}</li>
            </ul>
          </div>
        </div>
      </Section>

      <Section title={isEn ? '🔧 Parameters' : '🔧 Parametreler'}>
        <Param name={isEn ? 'RSI Dead Zone' : 'RSI Ölü Bölge'} unit={isEn ? 'points' : 'puan'} range="0 – 15" recommended={isEn ? '0 (off)' : '0 (kapalı)'} sweepEx="0,1,3" recommendedLabel={rec} rangeLabel={rng} sweepLabel={swp}>
          <p>{isEn
            ? "NOT an entry gate — it's a REVERSAL buffer around SMA (entry is unconditional, only the flip is delayed by this much)."
            : 'Bir giriş kapısı DEĞİLDİR — SMA\'nın etrafında bir YÖN DEĞİŞTİRME tamponudur (giriş koşulsuz, sadece flip bu kadar gecikir).'}</p>
          <Example label={isEn ? 'Example' : 'Örnek'}>{isEn ? 'Dead Zone=1, while LONG with RSI=20 SMA=19 → won\'t flip to SHORT until RSI is 18.' : "Ölü Bölge=1, LONG iken RSI=20 SMA=19 → RSI 18 olmadan SHORT'a dönmez."}</Example>
        </Param>
        <Param name={isEn ? 'TP Percentage' : 'TP Yüzdesi'} unit="%" recommended={isEn ? '0 (off)' : '0 (kapalı)'} sweepEx="0,1,2" recommendedLabel={rec} sweepLabel={swp}>
          <p>{isEn
            ? '0/blank = TP fully off (position closes only via SL or the SMA flip). >0 = a fixed percentage profit target from entry.'
            : '0/boş = TP tamamen kapalı (pozisyon yalnızca SL veya SMA flip\'iyle kapanır). >0 = girişten sabit yüzde kâr hedefi.'}</p>
        </Param>
        <Param name={isEn ? 'SL Percentage' : 'SL Yüzdesi'} unit="%" recommended={isEn ? '0 (off)' : '0 (kapalı)'} sweepEx="0,0.5,1" recommendedLabel={rec} sweepLabel={swp}>
          <p>{isEn
            ? "0/blank = SL fully off — unlike other strategies, there's NO ATR fallback here, so the position has no fixed price protection at all. >0 = a fixed percentage loss limit from entry."
            : "0/boş = SL tamamen kapalı — diğer stratejilerin aksine burada ATR fallback'ı YOKTUR, pozisyonun hiçbir sabit fiyat koruması olmaz. >0 = girişten sabit yüzde zarar sınırı."}</p>
        </Param>
      </Section>

      <Note>
        {isEn
          ? 'It closes and reopens in the opposite direction at the SAME time once crossed — trading continues without waiting. The Dead Zone is the only tool to reduce whipsaw; increasing it reduces the number of flips.'
          : "Geçince kapanır ve AYNI anda ters yönde yeniden açılır — işlem sürekli devam eder, bekleme yoktur. Whipsaw'ı azaltmak için tek araç Ölü Bölge'dir; artırınca flip sayısı azalır."}
      </Note>
    </>
  );
}

function Rsima50Tab({ isEn }) {
  const rec = isEn ? 'Recommended:' : 'Önerilen:';
  const rng = isEn ? 'Range:' : 'Aralık:';
  const swp = isEn ? 'Example in Parameter Sweep:' : "Parametre Taraması'nda örnek:";
  return (
    <>
      <Section title={isEn ? '📊 SMA - 50 Level — Overview' : '📊 SMA - 50 Seviyesi — Genel Bakış'}>
        <p className="sh-text">
          {isEn ? (
            <>Decides <strong>only at candle close</strong> (never on the live/intra-candle price, unlike RSI+SMA)
            by comparing SMA (the 14-period average of RSI) against a <strong>Dead Zone band</strong> around 50
            (width set by the Dead Zone parameter). <strong>Simple by design</strong>: the position simply rides for
            as long as SMA stays outside the band, and closes the instant it returns inside — no trailing/profit-lock
            logic yet (intentionally left for a future iteration). Opens instantly at bot startup too, in whichever
            direction SMA is currently on.</>
          ) : (
            <>SMA'yı (RSI'ün 14 periyotluk ortalaması) 50 etrafındaki bir <strong>Ölü Bölge bandıyla</strong>
            (genişlik Ölü Bölge parametresiyle belirlenir) karşılaştırarak <strong>SADECE mum kapanışında</strong>
            karar verir (RSI+SMA'nın aksine anlık/mum-içi fiyata asla bakmaz). <strong>Bilinçli olarak basit</strong>:
            pozisyon, SMA bandın dışında kaldığı sürece sürer, bandın içine döner dönmez kapanır — henüz bir
            trailing/kâr-kilitleme mantığı yok (bilinçli olarak sonraki bir aşamaya bırakıldı). Bot başlangıcında da
            anında, SMA o an hangi taraftaysa o yönde açılır.</>
          )}
        </p>
        <div className="sh-rsi-grid">
          <div className="sh-rsi-card sh-rsi-long">
            <div className="sh-rsi-card-title">▲ LONG</div>
            <div className="sh-rsi-range">SMA &gt; 50 + {isEn ? 'Dead Zone' : 'Ölü Bölge'}</div>
          </div>
          <div className="sh-rsi-card sh-rsi-short">
            <div className="sh-rsi-card-title">▼ SHORT</div>
            <div className="sh-rsi-range">SMA &lt; 50 − {isEn ? 'Dead Zone' : 'Ölü Bölge'}</div>
          </div>
        </div>
      </Section>

      <Timeline steps={isEn ? [
        { label: 'Entry (incl. bot startup)', text: 'At candle close, whenever SMA is outside the 50±Dead Zone band → opens instantly in that direction (no wait, even on the very first check after bot start).', color: '#3b82f6' },
        { label: 'While open', text: 'The position stays open for as long as SMA remains outside the band (unless TP/SL are set and get hit first).', color: '#64748b' },
        { label: 'Exit', text: 'Closes the instant SMA returns inside the 50±Dead Zone band — the exact mirror of the entry rule.', color: 'var(--accent)' },
        { label: 'TP/SL (optional)', text: 'Both are independently optional and OFF at 0/blank — unlike other strategies, there is no ATR fallback here, so a 0/blank SL means no fixed price protection at all.', color: '#dc2626' },
      ] : [
        { label: 'Giriş (bot açılışı dahil)', text: 'Mum kapanışında SMA, 50±Ölü Bölge bandının dışındaysa → o yönde anında açılır (bekleme yok, bot başladıktan sonraki ilk kontrolde bile).', color: '#3b82f6' },
        { label: 'Açıkken', text: 'Pozisyon, SMA bandın dışında kaldığı sürece açık kalır (TP/SL ayarlıysa ve önce tetiklenirse hariç).', color: '#64748b' },
        { label: 'Çıkış', text: 'SMA, 50±Ölü Bölge bandının içine döner dönmez kapanır — giriş kuralının tam simetriği.', color: 'var(--accent)' },
        { label: 'TP/SL (opsiyonel)', text: 'İkisi de bağımsız olarak opsiyoneldir, 0/boş ise KAPALIDIR — diğer stratejilerin aksine burada ATR fallback\'ı yoktur, yani SL 0/boş bırakılırsa hiçbir sabit fiyat koruması olmaz.', color: '#dc2626' },
      ]} />

      <Section title={isEn ? '🔧 Parameters' : '🔧 Parametreler'}>
        <Param name={isEn ? 'RSI Dead Zone' : 'RSI Ölü Bölge'} unit={isEn ? 'points' : 'puan'} range="0 – 15" recommended={isEn ? '0 (off)' : '0 (kapalı)'} sweepEx="0,3,5" recommendedLabel={rec} rangeLabel={rng} sweepLabel={swp}>
          <p>{isEn
            ? 'Sets BOTH the entry band width AND the exit threshold (they are the exact mirror of each other): SMA must move outside 50±this many points to open a trade, and returning inside that same band closes it. At 0, both collapse to the plain 50 line.'
            : "HEM giriş bandı genişliğini HEM çıkış eşiğini belirler (ikisi birbirinin tam simetriğidir): SMA'nın işlem açılması için 50±bu kadar puan dışına çıkması gerekir, aynı banda geri dönmesi de pozisyonu kapatır. 0 iken ikisi de düz 50 çizgisine daralır."}</p>
          <Example label={isEn ? 'Example' : 'Örnek'}>{isEn ? 'Dead Zone=5 → 45-55 is the band. LONG opens once SMA rises above 55, closes once SMA falls back to 55 or below.' : "Ölü Bölge=5 → 45-55 arası bant. SMA 55'in üzerine çıkınca LONG açılır, tekrar 55 veya altına düşünce kapanır."}</Example>
        </Param>
        <Param name={isEn ? 'TP Percentage' : 'TP Yüzdesi'} unit="%" recommended={isEn ? '0 (off)' : '0 (kapalı)'} sweepEx="0,1,2" recommendedLabel={rec} sweepLabel={swp}>
          <p>{isEn
            ? '0/blank = TP fully off (position closes only via SL or the Dead Zone return). >0 = a fixed percentage profit target from entry.'
            : '0/boş = TP tamamen kapalı (pozisyon yalnızca SL veya Ölü Bölge dönüşüyle kapanır). >0 = girişten sabit yüzde kâr hedefi.'}</p>
        </Param>
        <Param name={isEn ? 'SL Percentage' : 'SL Yüzdesi'} unit="%" recommended={isEn ? '0 (off)' : '0 (kapalı)'} sweepEx="0,0.5,1" recommendedLabel={rec} sweepLabel={swp}>
          <p>{isEn
            ? "0/blank = SL fully off — unlike other strategies, there's NO ATR fallback here, so the position has no fixed price protection at all. >0 = a fixed percentage loss limit from entry."
            : "0/boş = SL tamamen kapalı — diğer stratejilerin aksine burada ATR fallback'ı YOKTUR, pozisyonun hiçbir sabit fiyat koruması olmaz. >0 = girişten sabit yüzde zarar sınırı."}</p>
        </Param>
      </Section>

      <Note>
        {isEn
          ? 'RSI(14)/SMA(14) periods are fixed. TP and SL are both optional and fully independent (0/blank = off on either one) — no ATR Period/Multiplier or SL Cooldown fields exist for this strategy anymore.'
          : "RSI(14)/SMA(14) periyotları sabittir. TP ve SL ikisi de opsiyoneldir ve birbirinden tamamen bağımsızdır (herhangi biri 0/boş = kapalı) — bu strateji için artık ATR Periyodu/Çarpanı ya da SL Cooldown alanları yoktur."}
      </Note>
      <Warn>
        {isEn
          ? 'Known risk: on low timeframes (e.g. 1m-5m) with Dead Zone left at 0, SMA can flip sides very quickly on pure noise, causing frequent rapid-fire entries/exits — use a non-zero Dead Zone and/or a higher timeframe to mitigate this.'
          : "Bilinen risk: düşük zaman dilimlerinde (örn. 1dk-5dk) Ölü Bölge 0 bırakılırsa, SMA salt gürültüyle çok hızlı taraf değiştirebilir — bu da sık ve hızlı giriş-çıkışlara yol açar. Sıfırdan farklı bir Ölü Bölge ve/veya daha yüksek bir zaman dilimi kullanın."}
      </Warn>
    </>
  );
}

function TrendPullbackTab({ isEn }) {
  const rec = isEn ? 'Recommended:' : 'Önerilen:';
  const rng = isEn ? 'Range:' : 'Aralık:';
  const swp = isEn ? 'Example in Parameter Sweep:' : "Parametre Taraması'nda örnek:";
  return (
    <>
      <Section title={isEn ? '📐 Trend-Pullback + R — Overview' : '📐 Trend-Pullback + R — Genel Bakış'}>
        <p className="sh-text">
          {isEn ? (
            <>The trend filter is MA50 vs MA200 (the same lines always drawn on the chart). Entry: opens immediately if
            price is close to MA50 (pullback, "not overextended") and RSI momentum confirms. <strong>Simple by design</strong>:
            there is no fixed TP and no trailing yet (profit optimization is intentionally left for a future iteration) — the
            position simply rides for as long as the trend filter stays valid.</>
          ) : (
            <>Trend filtresi MA50 vs MA200'dür (grafikte zaten her zaman çizilen aynı çizgiler). Giriş:
            fiyat MA50'ye yakınken (pullback, "aşırı uzamamış") RSI momentum onayı verirse hemen açılır.
            <strong> Bilinçli olarak basit</strong>: sabit TP yoktur, henüz trailing de yoktur (kâr
            optimizasyonu bilinçli olarak sonraki bir aşamaya bırakıldı) — pozisyon, trend filtresi
            geçerli kaldığı sürece sürer.</>
          )}
        </p>
        <Timeline steps={isEn ? [
          { label: 'Entry', text: 'MA50>MA200 (uptrend) + price close to MA50 by the Pullback Distance + RSI ≥ RSI Momentum Threshold → LONG (symmetric for SHORT, futures only).', color: '#3b82f6' },
          { label: 'While open', text: 'The position rides for as long as the trend filter (MA50 vs MA200) stays valid — no fixed TP, no trailing yet.', color: '#64748b' },
          { label: 'Exit', text: 'Closes the instant MA50 crosses to the other side of MA200 (trend reversed) — a structural safety exit.', color: 'var(--accent)' },
          { label: 'Backstop', text: 'The fixed SL (Entry ∓ SL Multiplier×ATR, or SL%) stays active in parallel as a backstop for sudden/gap moves.', color: '#dc2626' },
        ] : [
          { label: 'Giriş', text: 'MA50>MA200 (yükseliş) + fiyat MA50\'ye Pullback Mesafesi kadar yakın + RSI ≥ RSI Momentum Eşiği → LONG (SHORT için simetrik, yalnızca vadeli).', color: '#3b82f6' },
          { label: 'Açıkken', text: 'Pozisyon, trend filtresi (MA50 vs MA200) geçerli kaldığı sürece sürer — sabit TP yok, henüz trailing de yok.', color: '#64748b' },
          { label: 'Çıkış', text: "MA50, MA200'ün karşı tarafına geçer geçmez (trend tersine döndü) kapanır — yapısal güvenlik çıkışı.", color: 'var(--accent)' },
          { label: 'Yedek Sigorta', text: 'Sabit SL (Giriş ∓ SL Çarpanı×ATR, ya da SL%) paralelde aktif kalır — ani/gap hareketler için yedek sigorta.', color: '#dc2626' },
        ]} />
      </Section>

      <Section title={isEn ? '🔧 Parameters' : '🔧 Parametreler'}>
        <Param name={isEn ? 'ADX Period' : 'ADX Periyodu'} unit="bar" range="7 – 30" recommended="14" sweepEx="9,14,21" recommendedLabel={rec} rangeLabel={rng} sweepLabel={swp}>
          <p>{isEn ? 'ADX calculation length — a lower value produces a more sensitive ADX, a higher value a more stable one.' : 'ADX hesaplama uzunluğu — düşük değer daha duyarlı, yüksek değer daha kararlı ADX üretir.'}</p>
        </Param>
        <Param name={isEn ? 'Pullback Distance' : 'Pullback Mesafesi'} unit="× ATR" range="0.3 – 2.0" recommended="0.7" sweepEx="0.5,0.7,1.0" recommendedLabel={rec} rangeLabel={rng} sweepLabel={swp}>
          <p>{isEn ? "The allowed distance of price from MA50 at entry." : "Girişte fiyatın MA50'ye izin verilen uzaklığı."}</p>
          <Example label={isEn ? 'Low (0.3-0.6)' : 'Düşük (0.3-0.6)'}>{isEn ? 'Only opens when genuinely close to MA50 — fewer but "cleaner" pullbacks.' : "Sadece MA50'ye gerçekten yakın anlarda açar — daha az ama daha \"temiz\" pullback."}</Example>
          <Example label={isEn ? 'High (1.5-2.0)' : 'Yüksek (1.5-2.0)'}>{isEn ? 'Opens even far from MA50 — more frequent but a weaker pullback definition.' : "MA50'den uzak da olsa açar — daha sık ama daha zayıf pullback tanımı."}</Example>
        </Param>
        <Param name={isEn ? 'RSI Momentum Threshold' : 'RSI Momentum Eşiği'} range="30 – 70" recommended="55" sweepEx="45,52,55" recommendedLabel={rec} rangeLabel={rng} sweepLabel={swp}>
          <p>{isEn ? 'Requires RSI ≥ this value for LONG, RSI ≤ (100 − this value) for SHORT.' : 'LONG için RSI ≥ bu değer, SHORT için RSI ≤ (100 − bu değer) aranır.'}</p>
          <Warn>
            {isEn ? (
              <><strong>Finding tested with real data:</strong> values BELOW 50 (e.g. the system default of 45) filter
              almost nothing and led to heavy losses in a real BTCUSDT backtest. A value CLEARLY ABOVE 50 (around 55)
              performed much better on real data (Profit Factor ~2.3). Above 60 it can become TOO strict, cutting trade
              count and quality (catching momentum after it's already exhausted). <strong>We recommend starting around
              55</strong>, don't rely on 45.</>
            ) : (
              <><strong>Gerçek veriyle test edilmiş bulgu:</strong> 50'nin ALTINDAKİ değerler (ör. sistem
              varsayılanı 45) neredeyse hiçbir şeyi filtrelemiyor ve gerçek BTCUSDT backtest'inde ağır
              zarara yol açtı. 50'nin BELİRGİN ÜZERİNDE bir değer (55 civarı) gerçek veride çok daha iyi
              sonuç verdi (Profit Factor ~2.3). 60'ın üzerinde ise AŞIRI sıkı hale gelip işlem sayısını
              çok azaltıp kaliteyi de düşürebiliyor (momentum tükendikten sonra yakalıyor). <strong>55
              civarından başlamanı öneririz</strong>, 45'e güvenme.</>
            )}
          </Warn>
        </Param>
        <Param name={isEn ? 'ADX Threshold' : 'ADX Eşiği'} range={isEn ? '0 (off) or 15 – 35' : '0 (kapalı) veya 15 – 35'} recommended="25" sweepEx="0,20,25" recommendedLabel={rec} rangeLabel={rng} sweepLabel={swp}>
          <p>{isEn
            ? <>The SAME shared field/convention as other strategies. The system default of 0 (off) allows trades to open even in WEAK/uncertain trends — around 25 performed clearly better on real data, <strong>don't rely on 0</strong>.</>
            : <>Diğer stratejilerle AYNI paylaşılan alan/konvansiyon. Sistem varsayılanı 0 (kapalı) olması ZAYIF/kararsız trendlerde de işlem açılmasına izin veriyor — gerçek veride 25 civarı belirgin şekilde daha iyi sonuç verdi, <strong>0'a güvenme</strong>.</>}</p>
        </Param>
      </Section>

    </>
  );
}

export default function SweepHelp({ onClose, initialTab }) {
  const { t, lang } = useTranslation();
  const isEn = lang === 'en';
  const TABS = [
    { id: 'range_hma_adx',    label: 'Range Bar+HMA+ADX' },
    { id: 'rsi_rsima',        label: 'RSI+SMA' },
    { id: 'rsima50',          label: 'SMA-50 Seviyesi' },
    { id: 'trend_pullback_r', label: 'Trend-Pullback+R' },
  ];
  const [tab, setTab] = useState(initialTab && TABS.some(tb => tb.id === initialTab) ? initialTab : TABS[0].id);
  const { offset: dragOffset, onMouseDown: handleHeaderMouseDown } = useDraggable();
  useTrackPageView(`Backtest Yardim - ${TABS.find(tb => tb.id === tab)?.label || tab}`);

  // Portal ile document.body'ye render edilir — bu modal, sürüklenebilir (transform'lu) Backtest
  // penceresinin İÇİNDEN açıldığı için, portal olmadan position:fixed overlay'in "viewport"u
  // Backtest'in kendi (dar) kutusuna daralır ve modal olması gerekenden çok küçük görünürdü.
  return createPortal(
    <div className="sh-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="sh-modal" style={{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }}>

        <div className="sh-header" onMouseDown={handleHeaderMouseDown}>
          <div>
            <h2 className="sh-title">{isEn ? 'Parameter Sweep Guide' : 'Parametre Taraması Rehberi'}</h2>
            <p className="sh-subtitle">{isEn ? "Each strategy's own parameters, full value ranges, and effects" : 'Her stratejinin kendi parametreleri, tüm değer aralıkları ve etkileri'}</p>
          </div>
          <button className="sh-close" onClick={onClose}>✕</button>
        </div>

        <div className="settings-tabs" style={{ padding: '0 24px', flexShrink: 0 }}>
          {TABS.map(tb => (
            <button
              key={tb.id}
              className={`settings-tab ${tab === tb.id ? 'active' : ''}`}
              onClick={() => setTab(tb.id)}
            >
              {tb.label}
            </button>
          ))}
        </div>

        <div className="sh-body">
          {tab === 'range_hma_adx'    && <HmaAdxTab rangeBar isEn={isEn} />}
          {tab === 'rsi_rsima'        && <RsiRsimaTab isEn={isEn} />}
          {tab === 'trend_pullback_r' && <TrendPullbackTab isEn={isEn} />}
          {tab === 'rsima50'          && <Rsima50Tab isEn={isEn} />}
          <CommonParams isEn={isEn} />
        </div>

        <div className="sh-footer">
          <button className="btn btn-start" onClick={onClose}>{t('common.understood')}</button>
        </div>

      </div>
    </div>,
    document.body
  );
}
